import Link from '@components/common/Link/Link'
import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import Button from '@components/ui/ButtonV2/Button'

import Media from '@components/ui/Media/Media'
import { formatPriceV2 } from '@lib/format-price'
import { GetCustomerOrdersQuery } from '@lib/graphql/graphql'
import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { generateMediaUrlV2 } from '@lib/utils'
import { selectCountry } from '@redux/globalSlice'
import { flattenInfiniteData, mapEdgesToItems } from '@utils/map'
import clsx from 'clsx'
import { Country } from 'config'
import { useInfiniteCustomerOrders } from 'features/account/api/get-customer-orders'
import { CSSProperties, FC, useEffect, useState } from 'react'
import { ImSpinner2 } from 'react-icons/im'
import { useSelector } from 'react-redux'
import s from './M59OrdersList.module.scss'

type M59OrdersListProps = {
  defaultNumberOfOrders?: number
  noOrdersText?: string
  ongoingOrders?: boolean
  previousOrders?: boolean
}

// export type OrderQueryResultType =
//   GetAllOrdersQuery['customer']['orders']['edges'][number]['node']

const M59OrdersList: FC<M59OrdersListProps> = ({
  defaultNumberOfOrders,
  noOrdersText,
  ongoingOrders,
  previousOrders,
  ...props
}) => {
  // get orders
  const { data, isFetching, hasNextPage, fetchNextPage } =
    useInfiniteCustomerOrders({
      variables: {
        first: defaultNumberOfOrders ?? 15,
      },
    })

  const country = useSelector(selectCountry)
  const orders = flattenInfiniteData(data).flatMap((result) =>
    mapEdgesToItems(result?.customer.orders),
  ) as GetCustomerOrdersQuery['customer']['orders']['edges'][number]['node'][]

  const [loading, setLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)

  useEffect(() => {
    if (initialLoad && !isFetching) {
      const timer = setTimeout(() => {
        setInitialLoad(false)
        setLoading(false)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [isFetching])

  const skeleton = Array.from({ length: defaultNumberOfOrders ?? 5 }).map(
    (_, i) => {
      return (
        <div className={clsx(s.skeleton, s.order)} key={i}>
          <div className={s.order__info}>
            {
              <ul>
                {Array.from({ length: 1 }).map((_, i) => {
                  return (
                    <li key={i}>
                      <div className={s.img} />
                    </li>
                  )
                })}
              </ul>
            }
            <div className={s.order__summary}>
              <Button size="small" className={s.order__view}>
                View or manage
              </Button>

              <div className={s.order__content}>
                <div className={s.order__info__title}>
                  ORDER ECO00000 on January 20, 1970
                </div>
                <p className={s.order__info__items}>x items &bull; xxxxx DKK</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
  )

  return (
    <div className={s.root}>
      {!loading && orders && orders.length > 0 ? (
        <>
          {ongoingOrders && (
            <OrdersList orders={orders} country={country} type={'ONGOING'} />
          )}
          {previousOrders && (
            <OrdersList orders={orders} country={country} type={'PREVIOUS'} />
          )}
        </>
      ) : loading ? (
        skeleton
      ) : (
        <TrustedHtml html={noOrdersText} />
      )}

      {hasNextPage && orders?.length > 0 && (
        <Button
          className={clsx(s.button, s.loadMore)}
          variant="menu"
          onClick={fetchNextPage}
          disabled={isFetching}
        >
          {isFetching ? <ImSpinner2 className={s['icon-spin']} /> : 'Load more'}
        </Button>
      )}
    </div>
  )
}

const OrdersList: FC<{
  orders: GetCustomerOrdersQuery['customer']['orders']['edges'][number]['node'][]
  country: Country
  type: 'ONGOING' | 'PREVIOUS'
}> = ({ orders, country, type }) => {
  if (type === 'PREVIOUS') {
    orders = orders.filter(isPreviousOrder)
  } else {
    orders = orders.filter((order) => !isPreviousOrder(order))
  }

  return (
    orders.length > 0 && (
      <div className={s.ordersWrapper}>
        <div className={s.ordersHeader}>
          {type === 'ONGOING' ? 'Ongoing orders' : 'Previous orders'}
        </div>
        {orders.map((order) => {
          const lineItems = mapEdgesToItems(order.lineItems)
          const flattenedLineItems = lineItems.flatMap((item) =>
            Array.from({ length: item.quantity }, () => item),
          )

          return (
            <div className={s.order} key={order.id}>
              <div className={s.order__info}>
                {order.lineItems && (
                  <ul
                    style={
                      {
                        '--line-items': lineItems.length,
                      } as CSSProperties
                    }
                  >
                    {lineItems.slice(0, 3).map((item, index) => (
                      <li key={index}>
                        <Link
                          className={s.variantLink}
                          link={
                            item.variant
                              ? {
                                  url: `/products/${item.productSlug}`,
                                  target: '_new',
                                }
                              : null
                          }
                          query={item.variant ? { itemNumber: item.sku } : null}
                        >
                          <div className={clsx(s.img)}>
                            {item.variant?.image?.src ? (
                              <Media
                                mediaWithCrops={
                                  {
                                    url: generateMediaUrlV2(
                                      item.variant.image?.src,
                                    ),
                                    media: {
                                      __typename: 'Image',
                                      url: generateMediaUrlV2(
                                        item.variant.image?.src,
                                      ),
                                    },
                                  } as MediaWithCropsFragment
                                }
                              />
                            ) : (
                              <div className={s.noImage}>
                                {item.productName}
                              </div>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                    {lineItems.length > 3 && (
                      <li className={s.lastIndex}>
                        +
                        {flattenedLineItems.length -
                          (lineItems.length > 3 ? 3 : lineItems.length)}
                      </li>
                    )}
                  </ul>
                )}
                <div className={s.order__summary}>
                  <Link link={{ url: '/account/order/' + order.id }}>
                    <Button size="small" className={s.order__view}>
                      View or manage
                    </Button>
                  </Link>

                  <div className={s.order__content}>
                    <div className={s.order__info__title}>
                      {order.externalReference && (
                        <div className={s.desktop__title}>
                          Order number: {order.externalReference}
                        </div>
                      )}
                      <div className={s.desktop__title}>
                        Date:{' '}
                        {new Date(order.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                      <div className={s.mobile__title}>
                        {order.externalReference && (
                          <>
                            Order {order.externalReference}
                            <br />
                          </>
                        )}
                        {new Date(order.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                    <p className={s.order__info__items}>
                      {flattenedLineItems.length > 1
                        ? `${flattenedLineItems.length} items`
                        : `1 item`}{' '}
                      | {formatPriceV2(country.code, order.total, 2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  )
}

function isOlderThanXDays(dateString: string, days: number) {
  const orderDate = new Date(dateString)
  const diffInMs = new Date().getTime() - orderDate.getTime()
  return diffInMs > days * 24 * 60 * 60 * 1000
}

function isPreviousOrder(
  order: GetCustomerOrdersQuery['customer']['orders']['edges'][number]['node'],
): boolean {
  if (['CANCELLED', 'REFUNDED'].includes(order.paymentState)) {
    return true
  }

  // if (order.trackingDetails?.length === 0) {
  //   return false
  // }

  if (order.shipmentState === 'DELIVERED') {
    return true
  }

  // if (
  //   order.shipmentState === 'PENDING' &&
  //   isOlderThanXDays(order.createdAt, 21)
  // ) {
  //   return true
  // }

  if (isOlderThanXDays(order.createdAt, 60)) {
    return true
  }

  return false
}

export default M59OrdersList
